import "quill/dist/quill.snow.css"; // Add css for snow theme
import React, { type Ref, useEffect, useImperativeHandle, useState } from "react";
import { useQuill } from "react-quilljs";
import type { Message } from "../../common/types/Message";
import { deltaToHtml, deltaToText } from "./convertDelta";
import "./QuillEditor.css";
import Button from "../Common/Buttons";

const modules = {
    toolbar: [],
};

const formats = ["link"];

type LinkEditProps = {
    selection?: string;
    onOk: (text: string, url: string | undefined) => void;
    onClose: () => void;
};

function hasValidLinkProtocol(url: string): boolean {
    return url.startsWith("http://") || url.startsWith("https://");
}

function LinkEdit(props: LinkEditProps) {
    const { onOk, onClose, selection } = props;
    const [text, setText] = useState<string>(selection || "");
    const [url, setUrl] = useState<string>("");
    const [error, setError] = useState<string>();

    function updateText(u: { target: { value: string } }) {
        setText(u.target.value);
    }

    function updateUrl(u: { target: { value: string } }) {
        setUrl(u.target.value);
        setError(undefined);
    }

    function handleAddLink() {
        if (!hasValidLinkProtocol(url)) {
            setError("Link must start with https:// or http://");
            return;
        }
        onOk(text, url);
    }

    return (
        <div className="quill-editor__link-edit">
            <div className="row">
                <input
                    type="text"
                    placeholder="Link text"
                    value={text}
                    onChange={updateText}
                />
            </div>
            <div className="row">
                <input
                    type="text"
                    placeholder="Insert URL"
                    value={url}
                    onChange={updateUrl}
                />
            </div>
            {error && <span className="quill-editor__link-edit__error">{error}</span>}
            <div className="row">
                <Button onClick={onClose}>Cancel</Button>
                <Button.N10 onClick={handleAddLink}>Add link</Button.N10>
            </div>
        </div>
    );
}

type QuillEditorProps = {
    placeholder?: string;
    className?: string;
    initialMessage?: string;
    initialMessageHtml?: string;
    disabled?: boolean;
    onSend: () => void;
    onChange: (text: string, html: string) => void;
    onLinkEditorClose: () => void;
};

export type QuillEditorActions = {
    createLink: (text: string, url: string) => void;
    deleteLink: (text: string) => void;
    addLink: () => void;
    clear: () => void;
    setMessage: (m: Message) => void;
};

const QuillEditor = React.forwardRef(
    (props: QuillEditorProps, refs: Ref<QuillEditorActions>) => {
        const {
            onChange,
            onLinkEditorClose,
            onSend,
            placeholder,
            className,
            initialMessage,
            initialMessageHtml,
            disabled,
        } = props;
        const [range, setRange] = useState<{ index: number; length: number }>();
        const [selection, setSelection] = useState<string>();
        const [linkEditorVisible, setLinkEditorVisible] = useState(false);

        const { quill, quillRef, Quill } = useQuill({
            modules,
            placeholder,
            formats,
        });

        useEffect(() => {
            if (quill) {
                if (initialMessageHtml) {
                    quill.clipboard.dangerouslyPasteHTML(initialMessageHtml);
                } else if (initialMessage) {
                    quill.setText(initialMessage);
                }
                quill.setSelection(quill.getLength(), 0);
                quill.on("text-change", () => {
                    const newContent = quill.getContents();
                    onChange(deltaToText(newContent), deltaToHtml(newContent));
                });
                quill.keyboard.bindings[13].unshift({
                    key: "Enter",
                    shortKey: true, // shortKey is a platform specific modifier equivalent to metaKey on a Mac and ctrlKey on Linux and Windows.
                    handler: onSend,
                });
            }
            if (Quill) {
                const Link = Quill.import("formats/link");
                const protocols = Link.PROTOCOL_WHITELIST as string[];
                if (!protocols.includes("albertineapp")) {
                    protocols.push("albertineapp");
                }
            }
        }, [Quill, quill]);

        useEffect(() => {
            if (quill) {
                quill.root.dataset.placeholder = placeholder;
            }
        }, [quill, placeholder]);

        const formatLink = (text: string, url: string) => {
            if (!quill) return;
            if (!range || range.length === 0) {
                quill.insertText(range ? range.index : 0, text, "link", url);
            } else if (text !== selection) {
                quill.deleteText(range.index, range.length);
                quill.insertText(range.index, text, "link", url);
            } else {
                quill.format("link", url);
            }
        };

        const showUrlDialogue = () => {
            if (!quill) return;
            quill.focus();
            const newRange = quill.getSelection();
            if (!newRange) {
                const emptyRange = quill.setSelection(0, 0);
                setRange(emptyRange);
                setSelection(quill.getText(0, 0));
            } else {
                setRange(newRange);
                setSelection(quill.getText(newRange.index, newRange.length));
            }
            setLinkEditorVisible(true);
        };

        useImperativeHandle(refs, () => ({
            addLink: () => {
                showUrlDialogue();
            },
            clear: () => {
                quill?.setText("");
            },
            setMessage: (msg: Message) => {
                if (!quill) return;
                if (msg.messageHtml) {
                    quill.clipboard.dangerouslyPasteHTML(msg.messageHtml);
                    quill.setSelection(msg.messageHtml.length, 0, "user");
                } else {
                    quill.setText(msg.message);
                    quill.setSelection(msg.message.length, 0, "user");
                }
            },
            createLink: (text: string, url: string) => {
                if (!quill) return;
                const newRange = quill.getSelection();
                if (newRange) {
                    // Focus is in the editor and respect user's cursor position
                    quill.insertText(newRange.index, text, "link", url);
                } else {
                    // Focus is not in the editor, add article links in the new line
                    const quillText = quill.getText();
                    quill.insertText(
                        quillText.length === 1 ? 0 : quillText.length,
                        text,
                        "link",
                        url,
                    );
                }
            },
            deleteLink: (text: string) => {
                const quillText = quill.getText();
                quill.deleteText(quillText.indexOf(text), text.length);
            },
        }));

        useEffect(() => {
            if (quill) {
                quill.getModule("toolbar").addHandler("link", showUrlDialogue);
            }
        }, [quill]);

        useEffect(() => {
            if (quill) {
                quill.enable(!disabled);
            }
        }, [quill, disabled]);

        return (
            <div className={className || ""}>
                <div ref={quillRef} className="quill-editor" />
                {linkEditorVisible && (
                    <LinkEdit
                        selection={selection}
                        onOk={(text, url) => {
                            formatLink(text, url || "");
                            setLinkEditorVisible(false);
                            onLinkEditorClose();
                        }}
                        onClose={() => {
                            setLinkEditorVisible(false);
                            onLinkEditorClose();
                        }}
                    />
                )}
            </div>
        );
    },
);

export default QuillEditor;
